import React, { useEffect, useCallback } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import gsap from 'gsap';

function IntroAnimation({ onComplete }) {
  const { camera, controls, scene } = useThree();

  const animateScene = useCallback(() => {
    const masterTimeline = gsap.timeline({
      defaults: { ease: "power2.inOut" }
    });

    // Handle grid and basic elements
    scene.traverse((object) => {
      if (object.material && !object.name.includes('hex')) {
        const originalOpacity = object.material.opacity || 1;
        object.material.opacity = 0;
        object.material.transparent = true;

        if (object.name.includes('grid')) {
          masterTimeline.to(object.material, {
            opacity: 0.3,
            duration: 1,
            ease: "power1.inOut"
          }, 0);
        } else if (object.name.includes('pyramid')) {
          masterTimeline.to(object.material, {
            opacity: originalOpacity,
            duration: 1,
            ease: "power2.inOut"
          }, 0.2);
        } else if (object.name.includes('dna')) {
          masterTimeline.to(object.material, {
            opacity: originalOpacity,
            duration: 1.5,
            ease: "power3.inOut"
          }, 0.4);
        }
      }
    });

    // Handle hexagon planes
    const hexGroups = [];
    scene.traverse((object) => {
      if (object.name.includes('reality-plane-')) {
        hexGroups.push(object);
      }
    });

    hexGroups.sort((a, b) => a.position.y - b.position.y);

    hexGroups.forEach((group, index) => {
      const fillMesh = group.children.find(child => child.name.includes('hex-fill'));
      const outlineMesh = group.children.find(child => child.name.includes('hex-outline'));
      
      if (fillMesh && outlineMesh) {
        const opacityFactor = 1 - (index / hexGroups.length * 0.3);
        const fillOpacity = 0.02 * opacityFactor;
        const outlineOpacity = 0.15 * opacityFactor;

        masterTimeline.to(fillMesh.material, {
          opacity: fillOpacity,
          duration: 0.8,
          ease: "power2.inOut",
          overwrite: false
        }, 0.4 + (index * 0.01));

        masterTimeline.to(outlineMesh.material, {
          opacity: outlineOpacity,
          duration: 0.8,
          ease: "power2.inOut",
          overwrite: false
        }, 0.4 + (index * 0.01));
      }
    });

    return masterTimeline;
  }, [scene]);

  useEffect(() => {
    if (!camera || !controls) return;

    controls.enabled = false;
    const finalPosition = new THREE.Vector3(50, 50, 50);
    const timeline = animateScene();

    const spiralPoints = [];
    const totalRotations = 1.25;
    const pointsPerRotation = 120;
    const totalPoints = totalRotations * pointsPerRotation;

    // Calculate the last spiral point to match final position
    const lastSpiralPoint = new THREE.Vector3(
      Math.cos(Math.PI * 2 * totalRotations) * 50,
      50,
      Math.sin(Math.PI * 2 * totalRotations) * 50
    );

    for (let i = 0; i <= totalPoints; i++) {
      const progress = i / totalPoints;
      const angle = progress * Math.PI * 2 * totalRotations;
      
      // Lerp between initial radius/height and final position values
      const radius = THREE.MathUtils.lerp(200, 50, Math.pow(progress, 0.6));
      const height = THREE.MathUtils.lerp(250, 50, Math.pow(progress, 0.8));
      const oscillation = Math.sin(progress * Math.PI * 3) * (1 - progress) * 2;
      
      const x = Math.cos(angle) * radius;
      const y = height + oscillation;
      const z = Math.sin(angle) * radius;
      
      // For the last point, ensure it matches the final position
      if (i === totalPoints) {
        spiralPoints.push(finalPosition);
      } else {
        spiralPoints.push(new THREE.Vector3(x, y, z));
      }
    }

    const curve = new THREE.CatmullRomCurve3(spiralPoints, false);
    curve.tension = 0.35; // Even smoother movement

    const duration = 2000; // Exactly 2 seconds
    const startTime = Date.now();
    let animationFrame;

    function animate() {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3); // Smoother easing
      
      const point = curve.getPoint(easeProgress);
      camera.position.copy(point); // Direct copy instead of lerp for smoother end
      camera.lookAt(0, 0, 0);
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        controls.enabled = true;
        controls.target.set(0, 0, 0);
        controls.update();
        if (onComplete) onComplete();
      }
    }

    animate();

    return () => {
      if (timeline) timeline.kill();
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      controls.enabled = true;
    };
  }, [camera, controls, animateScene, onComplete]);

  return null;
}

export default IntroAnimation;